import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Avatar,
  CircularProgress,
  Tooltip,
  Pagination,
  Button,
  Badge,
  FormControl,
  Select,
  MenuItem,
  Alert
} from "@mui/material";
import { useLocation } from "react-router-dom";

// アイコン
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import InfoIcon from '@mui/icons-material/Info';

// 文言管理（将来的な一元管理のための構造）
const messages = {
  productRegistrationRequired: {
    title: "残量データの表示には製品登録が必要です。",
    description: "製品を登録してください。",
    actionLabel: "製品を登録する"
  },
  allModeNotice: {
    description: "特定の製品を選択すると、残量データと設定完了済みの顧客のみが表示されます。"
  }
};

const ClientSection = ({
  clients,
  products,
  totalElements,
  loading,
  searchQuery,
  activeFilters,
  page,
  totalPages,
  selectedProductId,
  activeSorting,
  handlePageChange,
  handleProductSelect,
  getSortingText,
  setFilterOpen,
  setSortOpen,
  navigate
}) => {
  // 🚀 location を取得
  const location = useLocation();
  
  // 「すべて」が選択されているかどうか
  const isAllSelected = selectedProductId === 'all';

  // 残量に基づいて色を決定
  const getRemainingColor = (ratio) => {
    if (ratio === null || ratio === undefined) return "grey";
    if (ratio <= 20) return "#f44336"; // 危険: 赤
    if (ratio <= 40) return "#ff9800"; // 警告: オレンジ
    return "#4caf50"; // 良好: 緑
  };

  // 顧客名から適切なアバター表示を生成
  const getAvatarDisplay = (name) => {
    // 記号を飛ばして最初の文字・数字を取得（記号以外はすべて有効）
    const meaningfulChar = name.match(/[^\s\(\)\[\]\{\}「」『』（）【】〈〉《》〔〕｜＼／：；，．、。！？]/);
    
    if (meaningfulChar) {
      const char = meaningfulChar[0];
      // 文字に基づいてカラーを決定（一貫性のために文字コードを使用）
      const colorIndex = char.charCodeAt(0) % 8;
      const colors = [
        '#1976d2', '#388e3c', '#f57c00', '#d32f2f', 
        '#7b1fa2', '#303f9f', '#0097a7', '#689f38'
      ];
      
      return {
        char: char.toUpperCase(), // 英字は大文字に統一
        bgcolor: colors[colorIndex],
        color: 'white'
      };
    }
    
    // 意味のある文字が見つからない場合はPersonIconを使用
    return {
      icon: true,
      bgcolor: 'grey.200',
      color: 'text.primary'
    };
  };

  // 🚀 修正版：現在のURLパラメータをそのまま引き継ぐ
  const handleClientClick = (clientId) => {
    // location.search をそのまま使用することで、
    // 全ての絞り込み条件（製品選択、グループ、検索、フィルター、ページ等）を保持
    const url = `/client/${clientId}${location.search}`;
    navigate(url);
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        borderRadius: 2, 
        overflow: 'hidden' 
      }}
    >
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        px: 2, 
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          <Typography variant="h6" fontWeight="medium">
            顧客一覧
          </Typography>
        </Box>
        <Typography variant="body2">
          {totalElements}件中 {clients.length}件表示
        </Typography>
      </Box>

      {/* フィルター・ソートコントロール */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 0 }
        }}
      >
        <Box sx={{ 
          flexGrow: { xs: 1, sm: 0 },
          minWidth: { xs: '100px', sm: '120px' },
          maxWidth: { xs: '180px', sm: 'none' }
        }}>
          <FormControl size="small" fullWidth>
            <Select
              value={selectedProductId === 'all' ? 'all' : selectedProductId ?? 'all'}
              onChange={(e) => {
                const value = e.target.value;
                const productId = value === 'all' ? 'all' : Number(value);
                handleProductSelect(productId);
              }}
              displayEmpty
              variant="outlined"
              sx={{ 
                bgcolor: 'white',
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            >
              <MenuItem value='all'>すべて表示</MenuItem>
              {products
                .sort((a, b) => a.id - b.id) // IDでソートして最小IDを最初に
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: 0.5,
          flexShrink: 0
        }}>
          {/* 並び替えボタン */}
          <Tooltip 
            title={
              isAllSelected 
                ? "すべて表示では並び替えは利用できません" 
                : (getSortingText(activeSorting) || "並び替え")
            }
          >
            <span>
              <Button
                startIcon={<SortIcon sx={{ fontSize: '16px !important' }} />}
                variant="outlined"
                size="small"
                onClick={() => setSortOpen(true)}
                disabled={isAllSelected}
                sx={{
                  bgcolor: activeSorting && !isAllSelected ? 'primary.main' : 'white',
                  color: activeSorting && !isAllSelected ? 'white' : 'primary.main',
                  borderColor: 'primary.main',
                  fontSize: '0.75rem !important',
                  fontWeight: 500,
                  px: 1,
                  py: 0.25,
                  minWidth: '60px !important',
                  maxWidth: '80px',
                  height: '32px !important',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: activeSorting && !isAllSelected ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                    borderColor: 'primary.main'
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'grey.200 !important',
                    color: 'grey.500 !important',
                    borderColor: 'grey.300 !important'
                  }
                }}
              >
                並替
              </Button>
            </span>
          </Tooltip>
          
          {/* 絞り込みボタン */}
          <Tooltip title={activeFilters > 0 ? `${activeFilters}件のフィルターが適用中` : "絞り込み"}>
            <Badge badgeContent={activeFilters} color="primary">
              <Button
                startIcon={<TuneIcon sx={{ fontSize: '16px !important' }} />}
                variant="outlined"
                size="small"
                onClick={() => setFilterOpen(true)}
                sx={{
                  bgcolor: activeFilters > 0 ? 'primary.main' : 'white',
                  color: activeFilters > 0 ? 'white' : 'primary.main',
                  borderColor: 'primary.main',
                  fontSize: '0.75rem !important',
                  fontWeight: 500,
                  px: 1,
                  py: 0.25,
                  minWidth: '60px !important',
                  maxWidth: '80px',
                  height: '32px !important',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: activeFilters > 0 ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                    borderColor: 'primary.main'
                  }
                }}
              >
                絞込
              </Button>
            </Badge>
          </Tooltip>
        </Box>
      </Box>

      {/* 製品未登録時の案内表示 */}
      {products.length === 0 && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mx: 2, 
            mt: 2, 
            borderRadius: 1,
            '& .MuiAlert-message': {
              fontSize: '0.875rem',
              width: '100%'
            }
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant="inherit" sx={{ mb: 1 }}>
              {messages.productRegistrationRequired.title}
              {messages.productRegistrationRequired.description}
            </Typography>
            <Button 
              variant="contained"
              color="primary" 
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate("/product-registration")}
              aria-label="製品登録ページに移動"
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600
              }}
            >
              {messages.productRegistrationRequired.actionLabel}
            </Button>
          </Box>
        </Alert>
      )}

      {/* 「すべて」選択時の案内表示 */}
      {isAllSelected && products.length > 0 && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mx: 2, 
            mt: 2, 
            borderRadius: 1,
            '& .MuiAlert-message': {
              fontSize: '0.875rem',
              lineHeight: 1.5
            }
          }}
        >
          {messages.allModeNotice.description}
        </Alert>
      )}

      {/* ローディング表示 */}
      {loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            読み込み中...
          </Typography>
        </Box>
      )}

      {/* 顧客リスト空状態の文言改善 */}
      {!loading && clients.length === 0 ? (
        <Box 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            bgcolor: 'white' 
          }}
        >
          {searchQuery || activeFilters > 0 ? (
            <>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                検索結果はありません
              </Typography>
              <Typography color="text.secondary">
                {selectedProductId !== 'all' && products.length > 0
                  ? `「${products.find(p => p.id === selectedProductId)?.name}」の設定が完了している顧客は見つかりませんでした`
                  : '条件に一致する顧客は見つかりませんでした'
                }
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {selectedProductId !== 'all' && products.length > 0
                  ? `「${products.find(p => p.id === selectedProductId)?.name}」の設定が完了している顧客はいません`
                  : '顧客が登録されていません'
                }
              </Typography>
              {selectedProductId !== 'all' && products.length > 0 && (
                <Typography color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  製品設定を完了した顧客のみが表示されます
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/client-registration-step1")}
                sx={{ mt: 2 }}
              >
                顧客を登録する
              </Button>
            </>
          )}
        </Box>
      ) : !loading && (
        <>
          <List sx={{ p: 0, bgcolor: 'white' }}>
            {clients.map((client) => {
              // バックエンドから既に統合されたデータを使用
              const productSummary = client.productSummary;
              const avatarDisplay = getAvatarDisplay(client.name);
              
              // 🚀 新しい表示ロジック：機能フラグベース
              const renderProductSummary = () => {
                // 「すべて」選択時は何も表示しない
                if (isAllSelected) {
                  return null;
                }
                
                // productSummaryが存在しない = 製品設定なし → 何も表示しない
                if (!productSummary) {
                  return null;
                }
                
                // 🔹 予測機能が無効な場合は何も表示しない（UX重視）
                if (!productSummary.predictionEnabled) {
                  return null;
                }
                
                // 🔹 予測機能が有効な場合
                const hasPredictedQuantity = productSummary.predictedQuantity != null;
                const hasPredictedRatio = productSummary.predictedRatio != null;
                const remainingColor = getRemainingColor(productSummary.predictedRatio);
                
                if (hasPredictedQuantity) {
                  // ケース1: 予測残量あり
                  if (productSummary.tankInstalled && hasPredictedRatio) {
                    // フル機能：残量 + 割合
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WaterDropIcon sx={{ color: remainingColor, fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {productSummary.predictedQuantity}L
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocalGasStationIcon sx={{ color: remainingColor, fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {productSummary.predictedRatio.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    );
                  } else {
                    // 予測のみ：残量のみ表示
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <WaterDropIcon sx={{ color: remainingColor, fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          {productSummary.predictedQuantity}L
                        </Typography>
                      </Box>
                    );
                  }
                } else {
                  // ケース2: 予測機能ONだが予測残量なし = データ不足
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      データ不足
                    </Typography>
                  );
                }
              };
              
              // 🚀 危険表示の条件も機能フラグベースに変更
              const shouldShowDangerIndicator = () => {
                return !isAllSelected && 
                       productSummary?.predictionEnabled && 
                       productSummary?.predictedQuantity != null && 
                       productSummary?.predictedRatio != null &&
                       productSummary?.predictedRatio <= 20;
              };
              
              const showDanger = shouldShowDangerIndicator();
              
              return (
                <ListItem
                  key={client.id}
                  divider
                  disablePadding
                  sx={{ 
                    position: 'relative',
                    '&:last-child': { 
                      borderBottom: 'none' 
                    }
                  }}
                >
                  {/* 🚀 危険表示：予測機能ONかつ危険レベルの場合のみ */}
                  {showDanger && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        bgcolor: '#f44336',
                      }}
                    />
                  )}
                  <ListItemButton 
                    onClick={() => handleClientClick(client.id)}
                    sx={{ pl: showDanger ? 3 : 2 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: avatarDisplay.bgcolor,
                          color: avatarDisplay.color,
                          mr: 2,
                          width: 40,
                          height: 40,
                          fontSize: '1rem',
                          fontWeight: 'medium'
                        }}
                      >
                        {avatarDisplay.icon ? <PersonIcon fontSize="small" /> : avatarDisplay.char}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {client.name}
                        </Typography>
                        {/* 🚀 新しい表示ロジックを適用 */}
                        {renderProductSummary()}
                      </Box>
                      <KeyboardArrowRightIcon color="action" />
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {/* ページネーション */}
          {totalPages > 1 && (
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}>
              <Pagination
                count={totalPages}
                page={page + 1} // MUI Paginationは1から始まる
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default ClientSection;